// src/components/TokenListModal.js

import React, { useState, useEffect, useContext } from 'react';
import { formatUnits, Contract } from 'ethers';
import { WalletContext } from '../contexts/WalletContext';
import { supportedChains } from '../chains';
import { ETH_ADDRESS } from '../pages/Swap/utils/SwapConstants';
import secureFetch from '../utils/secureFetch';
import { useTranslation } from 'react-i18next';
import './TokenListModal.css';

// Custom tokens storage key
const CUSTOM_TOKENS_STORAGE_KEY = 'bitdogCustomTokens';

// Helper to create a native token object from chain config
const createNativeTokenForChain = (chainKey) => {
  const chain = supportedChains[chainKey];
  if (!chain || !chain.nativeToken) {
    console.error(`Missing chain configuration for ${chainKey}`);
    return null;
  }
  
  return {
    address: ETH_ADDRESS,
    symbol: chain.nativeToken.symbol,
    name: chain.nativeToken.name,
    decimals: chain.nativeToken.decimals || 18,
    logoURI: chain.nativeToken.icon
  };
};

const TokenListModal = ({ 
  isOpen, 
  onClose, 
  onSelectToken, 
  chain, // e.g., "bitrock" or "ethereum"
  userBalances, // optional: object mapping token addresses (lowercase) to balance (as string or number)
  fetchTokensByAddress // optional: chain-specific function to fetch tokens by contract address or name
}) => {
  const { t } = useTranslation();
  const { provider, address } = useContext(WalletContext);
  const [tokenList, setTokenList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [tokenBalances, setTokenBalances] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  
  // States for contract address search functionality
  const [foundToken, setFoundToken] = useState(null);
  const [customTokens, setCustomTokens] = useState({});
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [savedCustomTokens, setSavedCustomTokens] = useState({});
  const [showFoundToken, setShowFoundToken] = useState(false);
  const [hasBeenSaved, setHasBeenSaved] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [hasErrorFetching, setHasErrorFetching] = useState(false);
  const [currentChain, setCurrentChain] = useState(null);

  // Function to check if a string looks like a valid Ethereum/Bitrock address
  const isValidAddress = (address) => {
    return /^0x[a-fA-F0-9]{40}$/.test(address);
  };

  // Function to load custom tokens from localStorage
  const loadCustomTokens = () => {
    try {
      const storedTokens = localStorage.getItem(CUSTOM_TOKENS_STORAGE_KEY);
      if (storedTokens) {
        const parsedTokens = JSON.parse(storedTokens);
        // Only set tokens for the current chain
        if (parsedTokens[chain]) {
          setSavedCustomTokens(parsedTokens[chain]);
        } else {
          setSavedCustomTokens({});
        }
      }
    } catch (error) {
      console.error("Error loading custom tokens from localStorage:", error);
      setSavedCustomTokens({});
    }
  };

  // Helper function to handle BigInt serialization
  const convertBigIntToString = (obj) => {
    // Base case: if the value is null or undefined, return it as is
    if (obj === null || obj === undefined) {
      return obj;
    }
    
    // Handle BigInt directly
    if (typeof obj === 'bigint') {
      return obj.toString();
    }
    
    // Handle Date objects
    if (obj instanceof Date) {
      return obj.toISOString();
    }
    
    // Handle arrays recursively
    if (Array.isArray(obj)) {
      return obj.map(item => convertBigIntToString(item));
    }
    
    // Handle objects recursively
    if (typeof obj === 'object') {
      const result = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          result[key] = convertBigIntToString(obj[key]);
        }
      }
      return result;
    }
    
    // Return primitive values as is
    return obj;
  };

  // Function to save a custom token to localStorage
  const saveCustomToken = (token) => {
    try {
      // Check if token is valid
      if (!token || !token.address || !token.symbol || !token.name) {
        throw new Error("Invalid token data");
      }

      // Get existing tokens from localStorage
      const storedTokens = localStorage.getItem(CUSTOM_TOKENS_STORAGE_KEY);
      let parsedTokens = {};
      
      if (storedTokens) {
        parsedTokens = JSON.parse(storedTokens);
      }

      // Initialize chain object if it doesn't exist
      if (!parsedTokens[chain]) {
        parsedTokens[chain] = {};
      }

      // Add token to the current chain - convert any BigInt values to strings
      const addressLower = token.address.toLowerCase();
      const safeToken = convertBigIntToString(token);
      parsedTokens[chain][addressLower] = safeToken;

      // Save back to localStorage
      localStorage.setItem(CUSTOM_TOKENS_STORAGE_KEY, JSON.stringify(parsedTokens));

      // Update state
      setSavedCustomTokens(prev => ({
        ...prev,
        [addressLower]: safeToken
      }));

      setHasBeenSaved(true);
      
      return true;
    } catch (error) {
      console.error("Error saving custom token:", error);
      return false;
    }
  };

  // Function to remove a custom token from localStorage
  const removeCustomToken = (tokenAddress) => {
    try {
      const addressLower = tokenAddress.toLowerCase();
      
      // Get existing tokens from localStorage
      const storedTokens = localStorage.getItem(CUSTOM_TOKENS_STORAGE_KEY);
      if (!storedTokens) return false;
      
      const parsedTokens = JSON.parse(storedTokens);
      
      // Check if chain and token exist
      if (!parsedTokens[chain] || !parsedTokens[chain][addressLower]) return false;
      
      // Remove token
      delete parsedTokens[chain][addressLower];
      
      // Save back to localStorage
      localStorage.setItem(CUSTOM_TOKENS_STORAGE_KEY, JSON.stringify(parsedTokens));
      
      // Update state
      setSavedCustomTokens(prev => {
        const newTokens = {...prev};
        delete newTokens[addressLower];
        return newTokens;
      });
      
      return true;
    } catch (error) {
      console.error("Error removing custom token:", error);
      return false;
    }
  };

  // Function to fetch token information by contract address
  const fetchTokenByAddress = async (tokenAddress) => {
    if (!provider) return null;
    setSearchError(null);
    
    try {
      // Minimum ERC20 ABI for fetching token information
      const minABI = [
        "function name() view returns (string)",
        "function symbol() view returns (string)",
        "function decimals() view returns (uint8)"
      ];
      
      const tokenContract = new Contract(tokenAddress, minABI, provider);
      
      // Fetch token details
      const [name, symbol, decimals] = await Promise.all([
        tokenContract.name(),
        tokenContract.symbol(),
        tokenContract.decimals()
      ]);

      // Get token balance if available
      let balance = null;
      if (address) {
        try {
          const balanceABI = ["function balanceOf(address) view returns (uint256)"];
          const balanceContract = new Contract(tokenAddress, balanceABI, provider);
          const bal = await balanceContract.balanceOf(address);
          balance = formatUnits(bal, decimals);
        } catch (error) {
          console.warn("Could not fetch token balance:", error);
        }
      }
      
      const token = {
        address: tokenAddress,
        name,
        symbol,
        decimals,
        logoURI: 'https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/info/logo.png', // Default logo
        balance
      };

      return token;
    } catch (error) {
      console.error("Error fetching token information:", error);
      setSearchError(t('tokenModal.invalidToken'));
      return null;
    }
  };

  // Helper to shorten addresses (first 6 and last 4 characters)
  const shortenAddress = (addr) => {
    if (!addr) return addr;
    // Don't shorten the special native token address
    if (addr.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee') return t('wallet.nativeToken');
    return addr.length > 10 ? addr.slice(0, 6) + '...' + addr.slice(-4) : addr;
  };

  // FIXED: Clear search term when modal is closed
  const handleCloseModal = () => {
    // Clear search term and reset search state
    setSearchTerm('');
    setFoundToken(null);
    setShowFoundToken(false);
    setHasBeenSaved(false);
    setSearchError(null);
    
    // Call the original onClose function
    if (onClose) onClose();
  };

  // Load custom tokens when modal opens
  useEffect(() => {
    if (isOpen) {
      loadCustomTokens();
      setFoundToken(null);
      setShowFoundToken(false);
      setHasBeenSaved(false);
      setFetchError(null);
      setHasErrorFetching(false);
      // FIXED: Also clear search term when modal opens
      setSearchTerm('');
    }
  }, [isOpen, chain]);
  
  // Track chain changes and reset state
  useEffect(() => {
    if (chain !== currentChain) {
      // Reset all state when chain changes
      setTokenList([]);
      setTokenBalances({});
      setIsLoading(true);
      setFetchError(null);
      setHasErrorFetching(false);
      
      // Update the current chain reference
      setCurrentChain(chain);
    }
  }, [chain, currentChain]);

  // Effect to handle searching by contract address
  useEffect(() => {
    const searchByAddress = async () => {
      // Only proceed if the search term looks like an address
      if (!isValidAddress(searchTerm)) {
        setSearchError(null);
        setFoundToken(null);
        setShowFoundToken(false);
        setHasBeenSaved(false);
        return;
      }
      
      const addressLower = searchTerm.toLowerCase();
      
      // Check if this token is already in our lists
      const existsInTokenList = tokenList.some(token => token.address.toLowerCase() === addressLower);
      const existsInSavedTokens = savedCustomTokens[addressLower];
      
      if (existsInSavedTokens) {
        // If it's already saved, show it as found but mark as saved
        setFoundToken(savedCustomTokens[addressLower]);
        setShowFoundToken(true);
        setHasBeenSaved(true);
        return;
      }
      
      if (!existsInTokenList) {
        setIsSearchingAddress(true);
        setFoundToken(null);
        setShowFoundToken(false);
        setHasBeenSaved(false);
        
        const token = await fetchTokenByAddress(searchTerm);
        if (token) {
          setFoundToken(token);
          setShowFoundToken(true);
        }
        
        setIsSearchingAddress(false);
      }
    };
    
    if (searchTerm && searchTerm.length >= 42) {
      searchByAddress();
    } else {
      setFoundToken(null);
      setShowFoundToken(false);
      setHasBeenSaved(false);
    }
  }, [searchTerm, tokenList, savedCustomTokens]);

  // Fetch token list from remote endpoint based on chain.
  useEffect(() => {
    const fetchTokenList = async () => {
      setIsLoading(true);
      setFetchError(null);
      setHasErrorFetching(false);
      
      // Get chain configuration
      const chainConfig = supportedChains[chain];
      if (!chainConfig) {
        console.error(`No configuration found for chain: ${chain}`);
        setHasErrorFetching(true);
        
        // Reset tokens but ensure native token is added
        const nativeToken = createNativeTokenForChain(chain);
        if (nativeToken) {
          setTokenList([nativeToken]);
        } else {
          setTokenList([]);
        }
        
        setIsLoading(false);
        return;
      }
      
      // ALWAYS add native token regardless of other errors
      const nativeToken = createNativeTokenForChain(chain);
      let tokens = nativeToken ? [nativeToken] : [];
      
      // Get token list URL from chain config
      const url = chainConfig.tokenListUrl;
      if (!url) {
        console.error(`No tokenListUrl defined for chain: ${chain}`);
        setHasErrorFetching(true);
        setTokenList(tokens); // Set just the native token
        setIsLoading(false);
        return;
      }
      
      try {
        const response = await secureFetch(url);
        const data = await response.json();
        
        // Handle different token list formats
        if (Array.isArray(data)) {
          // Direct array format (like SushiSwap GitHub lists)
          tokens = [...tokens, ...data];
        } else {
          // Standard format with a 'tokens' array
          tokens = [...tokens, ...(data.tokens || [])];
        }
        
        // Limit tokens to a reasonable number (from chain config)
        if (tokens.length > (chainConfig.maxTokensToDisplay || 300)) {
          tokens = tokens.slice(0, chainConfig.maxTokensToDisplay || 300);
          
          // Make sure native token is still at the top if it was cut off
          if (nativeToken && !tokens.some(t => t.address === nativeToken.address)) {
            tokens[0] = nativeToken;
          }
        }
        
        // Sort tokens alphabetically by name
        tokens.sort((a, b) => {
          // Keep native token at the top
          if (a.address.toLowerCase() === ETH_ADDRESS.toLowerCase()) return -1;
          if (b.address.toLowerCase() === ETH_ADDRESS.toLowerCase()) return 1;
          
          return a.name.localeCompare(b.name);
        });
        
        setTokenList(tokens);
      } catch (err) {
        console.error("Error fetching token list:", err);
        setHasErrorFetching(true);
        // Still set the token list with just the native token
        setTokenList(tokens);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (isOpen) {
      fetchTokenList();
    }
  }, [chain, isOpen]);

  // IMPROVED: Fetch balances in smaller batches with robust error handling
  useEffect(() => {
    if (!address || !provider || !tokenList.length) return;
    
    // Clear balances when starting a new fetch
    setTokenBalances({});
    
    const fetchBalances = async () => {
      const balances = {};
      const ERC20ABI = ["function balanceOf(address) view returns (uint256)"];
      
      // Define a much smaller batch size to prevent "batch size too large" errors
      const BATCH_SIZE = 1000; 
      
      // Create a filtered list excluding the native token
      const erc20Tokens = tokenList.filter(token => 
        token.address.toLowerCase() !== ETH_ADDRESS.toLowerCase()
      );
      
      // Serial processing of tokens in small batches
      for (let i = 0; i < erc20Tokens.length; i += BATCH_SIZE) {
        const batch = erc20Tokens.slice(i, i + BATCH_SIZE);
        
        console.log(`Fetching balances batch ${Math.floor(i / BATCH_SIZE) + 1} of ${Math.ceil(erc20Tokens.length / BATCH_SIZE)} (tokens ${i+1}-${Math.min(i+BATCH_SIZE, erc20Tokens.length)})`);
        
        // Process each token in the batch SEQUENTIALLY instead of parallel Promise.all
        // This helps prevent "batch size too large" errors on some RPCs
        for (const token of batch) {
          try {
            const contract = new Contract(token.address, ERC20ABI, provider);
            const bal = await contract.balanceOf(address);
            balances[token.address.toLowerCase()] = bal.toString();
          } catch (error) {
            console.error(`Error fetching balance for ${token.symbol} (${token.address}):`, error);
            balances[token.address.toLowerCase()] = "0";
          }
          
          // Update state after each token for responsive UI
          setTokenBalances({...balances});
        }
        
        // Add a delay between batches to reduce RPC load
        if (i + BATCH_SIZE < erc20Tokens.length) {
          await new Promise(resolve => setTimeout(resolve, 50));
        }
      }
    };
    
    fetchBalances();
  }, [tokenList, provider, address]);

  // Combine standard tokens with saved custom tokens
  const getAllTokens = () => {
    // Convert saved custom tokens object to array
    const savedTokensArray = Object.values(savedCustomTokens);
    return [...tokenList, ...savedTokensArray];
  };

  // Filter tokens based on search term.
  const filteredTokens = getAllTokens().filter(token =>
    token.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
    token.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    token.symbol.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // No longer filtering out wrapped tokens
  const filteredWithoutWrapped = filteredTokens;

  // Sort tokens: native token always on top; then tokens with a nonzero balance; then alphabetical.
  const sortedTokens = filteredWithoutWrapped.slice().sort((a, b) => {
    if (a.address.toLowerCase() === ETH_ADDRESS.toLowerCase()) return -1;
    if (b.address.toLowerCase() === ETH_ADDRESS.toLowerCase()) return 1;
    const balanceA = parseFloat(tokenBalances[a.address.toLowerCase()] || (userBalances ? userBalances[a.address.toLowerCase()] || "0" : "0"));
    const balanceB = parseFloat(tokenBalances[b.address.toLowerCase()] || (userBalances ? userBalances[b.address.toLowerCase()] || "0" : "0"));
    if (balanceA > 0 && balanceB > 0) {
      return balanceB - balanceA;
    } else if (balanceA > 0) {
      return -1;
    } else if (balanceB > 0) {
      return 1;
    }
    return a.name.localeCompare(b.name);
  });

  // Handle click outside to close
  const handleOverlayClick = (e) => {
    if (e.target.className === 'modal-overlay') {
      handleCloseModal(); // FIXED: Use our new close handler
    }
  };

  // Handle escape key to close
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleCloseModal(); // FIXED: Use our new close handler
      }
    };
    
    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen]);

  // Handler for saving custom token
  const handleSaveToken = () => {
    if (foundToken) {
      const saved = saveCustomToken(foundToken);
      if (saved) {
        setHasBeenSaved(true);
      }
    }
  };

  if (!isOpen) return null;

  // Format balances for display
  const formatBalance = (rawBalance, decimals) => {
    try {
      const formatted = formatUnits(rawBalance, decimals);
      const num = parseFloat(formatted);
      if (num === 0) return "0";
      
      // For very small numbers, use scientific notation
      if (num < 0.000001) {
        return num.toExponential(4);
      }
      
      // For other numbers, format with appropriate precision
      return num.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 6
      });
    } catch (error) {
      return "0";
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="token-list-modal-content">
        <button className="token-modal-close-btn-x" onClick={handleCloseModal} aria-label={t('actions.close')}>
          ✕
        </button>
        
        <h2>{t('wallet.selectToken')}</h2>
        
        <div className="token-search-container">
          <span className="token-search-icon">🔍</span>
          <input
            type="text"
            placeholder={t('tokenModal.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="token-search-input"
            autoFocus
          />
        </div>
        
        {searchError && (
          <div className="token-search-error">
            {searchError}
          </div>
        )}

        {/* Display Found Token from Contract Search */}
        {showFoundToken && foundToken && (
          <div className="found-token-container">
            <div className="found-token-header">
              <h3>{t('tokenModal.tokenFound')}</h3>
              {!hasBeenSaved ? (
                <button className="save-token-btn" onClick={handleSaveToken}>
                  {t('tokenModal.saveToken')}
                </button>
              ) : (
                <span className="token-saved-badge">{t('tokenModal.saved')}</span>
              )}
            </div>
            <div 
              className="token-list-item found-token" 
              onClick={() => { onSelectToken(foundToken); handleCloseModal(); }} // FIXED: Use our new close handler
            >
              <img 
                src={foundToken.logoURI} 
                alt={foundToken.symbol} 
                className="token-icon"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/info/logo.png';
                }}
              />
              <div className="token-details">
                <span className="token-name-symbol">{foundToken.name} ({foundToken.symbol})</span>
                <span className="token-address">{shortenAddress(foundToken.address)}</span>
                {foundToken.balance && parseFloat(foundToken.balance) > 0 && (
                  <span className="token-balance-label">
                    {t('swap.balance')}: {parseFloat(foundToken.balance).toFixed(6)}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
        
        <div className="token-list">
          {isLoading ? (
            <div className="no-tokens-message">{t('tokenModal.loadingTokens')}</div>
          ) : isSearchingAddress ? (
            <div className="token-loading-item">
              <div className="token-loading-spinner"></div>
              <div>{t('tokenModal.searchingContract')}</div>
            </div>
          ) : sortedTokens.length > 0 ? (
            sortedTokens.map(token => {
              // Check if this is a custom saved token
              const isCustomToken = savedCustomTokens[token.address.toLowerCase()];
              
              return (
                <div 
                  key={token.address} 
                  className={`token-list-item ${isCustomToken ? 'custom-token' : ''}`}
                  onClick={() => { onSelectToken(token); handleCloseModal(); }} // FIXED: Use our new close handler
                >
                  <img 
                    src={token.logoURI} 
                    alt={token.symbol} 
                    className="token-icon"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/info/logo.png';
                    }}
                  />
                  <div className="token-details">
                    <div className="token-name-row">
                      <span className="token-name-symbol">{token.name} ({token.symbol})</span>
                      {isCustomToken && (
                        <span className="custom-token-badge">{t('tokenModal.custom')}</span>
                      )}
                    </div>
                    <span className="token-address">{shortenAddress(token.address)}</span>
                    {token.address.toLowerCase() !== ETH_ADDRESS.toLowerCase() &&
                      tokenBalances[token.address.toLowerCase()] &&
                      parseFloat(tokenBalances[token.address.toLowerCase()]) > 0 && (
                        <span className="token-balance-label">
                          {t('swap.balance')}: {formatBalance(tokenBalances[token.address.toLowerCase()], token.decimals)}
                        </span>
                    )}
                  </div>
                  {isCustomToken && (
                    <button 
                      className="remove-token-btn" 
                      onClick={(e) => {
                        e.stopPropagation();
                        removeCustomToken(token.address);
                      }}
                      title={t('tokenModal.removeCustomToken')}
                    >
                      ✕
                    </button>
                  )}
                </div>
              );
            })
          ) : (
            <div className="no-tokens-message">
              {isValidAddress(searchTerm) 
                ? t('tokenModal.noMatchingContractToken')
                : t('tokenModal.noTokensFound')}
            </div>
          )}
        </div>
        
        {/* Keeping this for backward compatibility, but it's hidden with CSS */}
        <button className="token-modal-close-btn" onClick={handleCloseModal}>{t('actions.close')}</button>
      </div>
    </div>
  );
};

export default TokenListModal;